import { useState } from 'react';
import { Download } from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { messageRefus } from '../financements/FacturesDuContrat';
import {
  exporterFactures,
  type FormatExportFactures,
  type JeuExportFactures,
  type LigneSuiviFacture,
  type ReferentielExportFactures,
} from '../../lib/useFactures';

/** Le dernier choix, gardé dans ce navigateur : l'export revient chaque mois avec les mêmes colonnes. */
const CLE_STOCKAGE = 'top_formation_export_factures';

interface Choix {
  jeu: JeuExportFactures;
  format: FormatExportFactures;
  colonnes: Partial<Record<JeuExportFactures, string[]>>;
}

function lireChoix(): Partial<Choix> {
  try {
    const stocke = window.localStorage.getItem(CLE_STOCKAGE);
    return stocke ? (JSON.parse(stocke) as Partial<Choix>) : {};
  } catch {
    // Stockage désactivé ou valeur illisible : on repart du défaut.
    return {};
  }
}

function enregistrerChoix(choix: Choix): void {
  try {
    window.localStorage.setItem(CLE_STOCKAGE, JSON.stringify(choix));
  } catch {
    // Stockage inaccessible : le choix vaut pour cette fenêtre seulement.
  }
}

/** Le choix gardé, ramené au référentiel du jour : une colonne retirée depuis n'est plus proposée. */
function choixInitial(referentiel: ReferentielExportFactures): Choix {
  const garde = lireChoix();
  const jeu = referentiel.jeux.find((un) => un.valeur === garde.jeu)?.valeur ?? referentiel.jeux[0]!.valeur;
  const format = referentiel.formats.find((un) => un.valeur === garde.format)?.valeur ?? referentiel.formats[0]!.valeur;
  const colonnes: Choix['colonnes'] = {};
  for (const un of referentiel.jeux) {
    const connues = new Set(un.colonnes.map((colonne) => colonne.cle));
    const gardees = garde.colonnes?.[un.valeur]?.filter((cle) => connues.has(cle));
    colonnes[un.valeur] = gardees && gardees.length > 0 ? gardees : un.colonnes.map((colonne) => colonne.cle);
  }
  return { jeu, format, colonnes };
}

const LEGENDE = 'mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray';

/**
 * Export de la liste des factures (TF-423, sans Tiime) : un fichier à la fois
 * — les pièces émises ou leurs règlements —, au format et avec les colonnes
 * choisis. Il porte sur la liste **filtrée**, toutes pages comprises ; les
 * brouillons n'en sortent pas. Fichiers, colonnes et formats viennent de
 * l'API (`domain/export-factures.ts`).
 */
export function ModaleExportFactures({
  referentiel,
  lignes,
  site,
  onFermer,
}: {
  referentiel: ReferentielExportFactures;
  /** La liste filtrée, toutes pages comprises. */
  lignes: LigneSuiviFacture[];
  site: string;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const [choix, setChoix] = useState<Choix>(() => choixInitial(referentiel));
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const jeu = referentiel.jeux.find((un) => un.valeur === choix.jeu) ?? referentiel.jeux[0]!;
  const cochees = new Set(choix.colonnes[jeu.valeur] ?? []);
  const nombreCochees = jeu.colonnes.filter((colonne) => colonne.obligatoire || cochees.has(colonne.cle)).length;
  const emises = lignes.filter((ligne) => ligne.statut === 'EMISE');
  const brouillons = lignes.length - emises.length;
  const factures = emises.filter((ligne) => ligne.nature === 'FACTURE').length;
  const tropLongue = emises.length > referentiel.piecesMax;
  const refus =
    emises.length === 0
      ? 'Aucune pièce émise dans la liste filtrée : les brouillons ne s’exportent pas.'
      : jeu.valeur === 'REGLEMENTS' && factures === 0
        ? 'Aucune facture émise dans la liste filtrée : un avoir n’a pas de règlement.'
        : tropLongue
          ? `${referentiel.piecesMax} pièces au plus par export : restreindre la liste (période, client) avant d’exporter.`
          : null;

  const changer = (suite: Partial<Choix>): void => {
    setErreur(null);
    setChoix((avant) => ({ ...avant, ...suite }));
  };
  const colonnesDuJeu = (cles: string[]): void =>
    changer({ colonnes: { ...choix.colonnes, [jeu.valeur]: cles } });
  const basculer = (cle: string): void => {
    const suivantes = new Set(cochees);
    if (suivantes.has(cle)) suivantes.delete(cle);
    else suivantes.add(cle);
    colonnesDuJeu(jeu.colonnes.map((colonne) => colonne.cle).filter((une) => suivantes.has(une)));
  };

  const exporter = async (): Promise<void> => {
    if (refus) return;
    setEnCours(true);
    setErreur(null);
    try {
      const colonnes = jeu.colonnes
        .filter((colonne) => colonne.obligatoire || cochees.has(colonne.cle))
        .map((colonne) => colonne.cle);
      const resultat = await exporterFactures({
        jeu: jeu.valeur,
        format: choix.format,
        colonnes,
        site,
        ids: emises.map((ligne) => ligne.id),
      });
      enregistrerChoix(choix);
      toast(
        `Fichier exporté — ${resultat.lignes} ${jeu.valeur === 'PIECES' ? 'pièce' : 'règlement'}${
          resultat.lignes > 1 ? 's' : ''
        }`,
      );
      onFermer();
    } catch (refusApi) {
      setErreur(messageRefus(refusApi, 'L’export n’a pas pu être produit.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      ouverte
      titre="Exporter la liste"
      sousTitre="Un fichier par export, sur la liste filtrée — toutes pages comprises"
      onFermer={onFermer}
      largeur="max-w-2xl"
    >
      <div className="space-y-5 p-4 sm:p-6">
        <fieldset>
          <legend className={LEGENDE}>Fichier</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {referentiel.jeux.map((un) => {
              const actif = un.valeur === jeu.valeur;
              return (
                <label
                  key={un.valeur}
                  className={`flex min-w-0 cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                    actif ? 'border-codialis-navy bg-codialis-sand' : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="jeu-export-factures"
                    className="mt-0.5 shrink-0"
                    checked={actif}
                    onChange={() => changer({ jeu: un.valeur })}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-codialis-navy">{un.libelle}</span>
                    <span className="block text-[11px] text-codialis-gray">{un.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset>
          <legend className={LEGENDE}>Format</legend>
          <div className="flex flex-wrap gap-2">
            {referentiel.formats.map((un) => {
              const actif = un.valeur === choix.format;
              return (
                <label
                  key={un.valeur}
                  className={`flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                    actif
                      ? 'border-codialis-navy bg-codialis-navy text-white'
                      : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="format-export-factures"
                    className="sr-only"
                    checked={actif}
                    onChange={() => changer({ format: un.valeur })}
                  />
                  <span className="min-w-0">{un.libelle}</span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset>
          <legend className={LEGENDE}>
            Colonnes · {nombreCochees} sur {jeu.colonnes.length}
          </legend>
          <div className="mb-1 flex flex-wrap gap-x-3">
              <button
                type="button"
                onClick={() => colonnesDuJeu(jeu.colonnes.map((colonne) => colonne.cle))}
                className="min-h-[32px] text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
              >
                Tout cocher
              </button>
              <button
                type="button"
                onClick={() => colonnesDuJeu([])}
                className="min-h-[32px] text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
              >
                Obligatoires seules
              </button>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-3">
            {jeu.colonnes.map((colonne) => (
              <label
                key={colonne.cle}
                className={`flex min-h-[32px] min-w-0 items-center gap-2 text-xs [@media(pointer:coarse)]:min-h-[44px] ${
                  colonne.obligatoire ? 'cursor-default text-codialis-gray' : 'cursor-pointer text-codialis-navy'
                }`}
                title={colonne.obligatoire ? 'Colonne obligatoire : elle désigne la ligne' : undefined}
              >
                <input
                  type="checkbox"
                  className="shrink-0 rounded"
                  checked={colonne.obligatoire || cochees.has(colonne.cle)}
                  disabled={colonne.obligatoire}
                  onChange={() => basculer(colonne.cle)}
                />
                <span className="min-w-0">
                  {colonne.libelle}
                  {colonne.obligatoire && <span className="text-[11px]"> · obligatoire</span>}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="space-y-1 rounded-xl bg-gray-50 p-3 text-[11px] text-codialis-gray">
          <p>
            <span className="font-semibold text-codialis-navy">
              {emises.length} pièce{emises.length > 1 ? 's' : ''} émise{emises.length > 1 ? 's' : ''}
            </span>{' '}
            dans la liste filtrée
            {brouillons > 0
              ? ` · ${brouillons} brouillon${brouillons > 1 ? 's' : ''} non exporté${brouillons > 1 ? 's' : ''}`
              : ''}
            .
          </p>
          <p>
            {jeu.valeur === 'PIECES'
              ? 'Un avoir sort en négatif. Montants et état de paiement sont ceux du jour.'
              : `Les règlements saisis sur ${factures} facture${factures > 1 ? 's' : ''}, sans les règlements retirés.`}
          </p>
        </div>

        {(refus ?? erreur) && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {refus ?? erreur}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal onClick={() => void exporter()} disabled={enCours || refus !== null}>
            <Download className="mr-1.5 h-4 w-4" aria-hidden />
            {enCours ? 'Export…' : 'Exporter'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}
